import dayjs from 'dayjs';

export type ContestPhaseDateRow = {
    phase: string;
    date_started: string;
    expiration_date: string;
};

export const todayStart = () => dayjs().startOf('day');

export function todayYmd(): string {
    return todayStart().format('YYYY-MM-DD');
}

export function datePickerValueToYyyyMmDd(val: string): string {
    if (!val) return '';
    const d = dayjs(String(val));
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

/** Returns the later of two YYYY-MM-DD strings. */
export function maxDateYmd(a: string, b: string): string {
    const da = dayjs(a).startOf('day');
    const db = dayjs(b).startOf('day');
    if (!da.isValid()) return b;
    if (!db.isValid()) return a;
    return da.isAfter(db) ? a : b;
}

export function clampDateToMin(ymd: string, minYmd: string): string {
    if (!ymd) return '';
    const d = dayjs(ymd).startOf('day');
    const min = dayjs(minYmd).startOf('day');
    if (!d.isValid()) return '';
    if (d.isBefore(min)) return min.format('YYYY-MM-DD');
    return d.format('YYYY-MM-DD');
}

export function isDateOnOrAfterToday(ymd: string): boolean {
    const d = dayjs(ymd).startOf('day');
    return d.isValid() && !d.isBefore(todayStart());
}

/** Shift entire timeline forward so the earliest phase starts today or later. */
export function shiftPhasesToToday(phases: ContestPhaseDateRow[]): ContestPhaseDateRow[] {
    if (!phases.length) return phases;

    const today = todayStart();
    let earliest = dayjs(phases[0].date_started).startOf('day');
    phases.forEach(p => {
        const s = dayjs(p.date_started).startOf('day');
        if (s.isValid() && s.isBefore(earliest)) earliest = s;
    });

    if (!earliest.isValid() || !earliest.isBefore(today)) {
        return phases.map(sanitizePhaseRow);
    }

    const shiftDays = today.diff(earliest, 'day');
    return phases.map(p => ({
        ...p,
        date_started: dayjs(p.date_started).add(shiftDays, 'day').format('YYYY-MM-DD'),
        expiration_date: dayjs(p.expiration_date).add(shiftDays, 'day').format('YYYY-MM-DD'),
    }));
}

export function sanitizePhaseRow(row: ContestPhaseDateRow): ContestPhaseDateRow {
    const today = todayYmd();
    const start = row.date_started ? clampDateToMin(row.date_started, today) : '';
    const minEnd = start ? maxDateYmd(today, start) : today;
    const end = row.expiration_date ? clampDateToMin(row.expiration_date, minEnd) : '';
    return { ...row, date_started: start, expiration_date: end };
}

export function sanitizePhaseRows(rows: ContestPhaseDateRow[]): ContestPhaseDateRow[] {
    return shiftPhasesToToday(rows.map(sanitizePhaseRow));
}
